import React, {useState} from "react";
import {getProfileUrl} from "#/src/utils/profile-url";
import {TimeDisplay} from "#/src/components/TimeDisplay";
import eye from "#/src/assets/svg/eye.svg";
import flag from "#/src/assets/svg/flag.svg";
import flagReported from "#/src/assets/svg/flag-reported.svg";
import {ApiArticleComment, ApiBlogPostData} from "#/src/definitions/ApiDefinitions";
import {Sort} from "#/src/utils/sort-utils";
import message from "#/src/assets/svg/message.svg";
import {usePageContext} from "vike-react/usePageContext";
import {AppPageContext, DataWrapped} from "#/src/definitions/AppDefinitions";
import {useData} from "vike-react/useData";
import {toast} from "react-toastify";
import AppConstants from "#/src/utils/app-constants";
import {composeHeaders} from "#/src/utils/api-request-utils";
import styled from "@emotion/styled";
import {AvatarWithFallback} from "#/src/components/AvatarWithFallback";

const PendingModeration = styled.p`
  color: hsla(0, 0%, 100%, .75);
`;

interface Props {
  comments: ApiArticleComment[],
  comment: ApiArticleComment,
  replyBox: ApiArticleComment | null,
  setReplyBox: (comment: ApiArticleComment | null) => void,
  onPost: (value: string) => void,
  nesting?: number
}

export function Comment({comments, comment, replyBox, setReplyBox, onPost, nesting = 1}: Props) {
  const pageContext = usePageContext() as AppPageContext;
  const {user} = pageContext;
  const {data: postData} = useData() as DataWrapped<ApiBlogPostData>;
  const [reported, setReported] = useState(comment.reported);

  async function onReport() {
    const response = await fetch(`${AppConstants.API_ROUTE}/blog/comments/${comment.id}/report`, {
      method: "POST",
      headers: composeHeaders(pageContext),
    });
    if (response.ok) {
      setReported(true);
    } else {
      const errorResponse = await response.json() as any;
      if (errorResponse.errors) {
        errorResponse.errors.forEach(err => toast(err));
      }
    }
  }

  function render() {
    return <>
      <div className={`msg ${comment.pendingModeration ? "msg-moderated" : ""} ${nesting > 1 ? `msg-nesting msg-nesting-${nesting}` : ""}`}>
        <div className="msg-author">
          <div className="author">
            <AvatarWithFallback user={comment.author} className={"avatar avatar-40"}/>
            <a href={getProfileUrl(comment.author)} className="author-link">{comment.author.username}</a>
          </div>
          <span className="msg-date"><TimeDisplay date={comment.creationDate}/></span>
        </div>
        <div className="msg-content">
          <p className="msg-text">{comment.message}</p>
          {comment.pendingModeration && <PendingModeration className={"msg-text"}>
            <img src={eye} alt={"Icon"} className={"me-1"}/> Oczekuje na moderację
          </PendingModeration>}
        </div>
        {renderButtons()}
      </div>
      {renderReplyBox()}
      {renderRepliesRecursive(nesting)}
    </>
  }

  function renderRepliesRecursive(level = 1) {
    return Sort.byDate(comments.filter(c => comment.id === c.replyTo)).map((articleComment: ApiArticleComment) => {
      return <Comment key={comment.id} comments={comments} comment={articleComment} replyBox={replyBox} setReplyBox={setReplyBox} onPost={onPost} nesting={level + 1}/>
    });
  }

  function renderButtons() {
    if (!user.loggedIn || comment.pendingModeration) {
      return <></>
    }
    return <div className="msg-footer">
      <button type="button" className="btn btn-ghost" onClick={() => setReplyBox(comment)}>
        <img src={message} alt="#" className={"me-2"}/>
        Odpowiedz
      </button>
      {renderReportButton()}
    </div>
  }

  function renderReportButton() {
    if (postData.article.commentsModerated) {
      return <></>
    }
    if (reported) {
      return <button type="button" className="btn btn-ghost">
        <img src={flagReported} alt="#" className={"me-2"}/>
        Zgłoszony
      </button>
    }
    return <button type="button" className="btn btn-ghost" onClick={onReport}>
      <img src={flag} alt="#" className={"me-2"}/>
      Zgłoś
    </button>
  }

  function renderReplyBox() {
    if (!user.loggedIn || comment.pendingModeration) {
      return <></>
    }
    if (replyBox == null || replyBox.id != comment.id) {
      return <></>
    }
    return <div className={`msg-answer ${nesting > 1 ? `msg-nesting msg-nesting-${nesting}` : ""}`}>
      <div className="author">
        <AvatarWithFallback user={user.data!} className={"avatar avatar-40"}/>
      </div>
      <input type="text" className="form-control" placeholder="Odpowiadasz na komentarz" id={`formReplyTo${comment.id}`}/>
      <button type={"button"} className={"btn btn-ghost"} onClick={() => onPost(comment.id)}>
        Dodaj komentarz
      </button>
    </div>
  }

  return render();
}